/**
 * Generic finite-difference numerical helpers for the NN-Newton design-optimiser core
 * (see foil-design-optimizer-DESIGN.md §8). Pure: no NN weights, no DOM, no state.
 *
 * Central differences throughout (O(h^2) accuracy). The step `h` may be a scalar
 * (applied to every component) or a per-component vector. Components with h<=0 fall
 * back to the default step.
 */

const DEFAULT_H = 1e-6;

/** Resolve a scalar/vector/undefined step spec into a per-component step array. */
function steps(h: number | number[] | undefined, n: number): number[] {
  const out = new Array<number>(n);
  if (Array.isArray(h)) {
    for (let j = 0; j < n; j++) { const hv = h[j]; out[j] = hv !== undefined && hv > 0 ? hv : DEFAULT_H; }
  } else {
    const s = h !== undefined && h > 0 ? h : DEFAULT_H;
    for (let j = 0; j < n; j++) out[j] = s;
  }
  return out;
}

/**
 * Central-difference Jacobian of a vector residual r:R^n -> R^m.
 * Returns J[i][j] = ∂r_i/∂v_j, an m×n matrix.
 */
export function jacobianFD(
  residual: (v: number[]) => number[],
  v: number[],
  h?: number | number[],
): number[][] {
  const n = v.length;
  const hj = steps(h, n);
  const r0 = residual(v);
  const m = r0.length;
  const J: number[][] = Array.from({ length: m }, () => new Array<number>(n));
  const vp = v.slice();
  for (let j = 0; j < n; j++) {
    const step = hj[j]!;
    const vj = v[j]!;
    vp[j] = vj + step;
    const rp = residual(vp);
    vp[j] = vj - step;
    const rm = residual(vp);
    vp[j] = vj;
    const inv = 1 / (2 * step);
    for (let i = 0; i < m; i++) J[i]![j] = (rp[i]! - rm[i]!) * inv;
  }
  return J;
}

/**
 * Central-difference gradient of a scalar field f:R^n -> R.
 * Returns g[j] = ∂f/∂v_j.
 */
export function gradFD(
  f: (v: number[]) => number,
  v: number[],
  h?: number | number[],
): number[] {
  const n = v.length;
  const hj = steps(h, n);
  const g = new Array<number>(n);
  const vp = v.slice();
  for (let j = 0; j < n; j++) {
    const step = hj[j]!;
    const vj = v[j]!;
    vp[j] = vj + step;
    const fp = f(vp);
    vp[j] = vj - step;
    const fm = f(vp);
    vp[j] = vj;
    g[j] = (fp - fm) / (2 * step);
  }
  return g;
}
